import { useMemo } from 'react'
import type { GeoJsonGeometry, Pos } from '../lib/geo.ts'
import { useMerged } from '../sync/hooks.ts'
import { isOpen } from '../sync/projectors.ts'
import { useAreas, useAreaVersions, type AreaVersionRow } from './geo.ts'
import { bearingDeg, distanceM, targetPoint } from './guide.ts'

/** M25-L (docs/M25.md C, rules 5 and 6; ADR-132): THE LINE OF PLAY of an area — pure, tested in playLine.test.ts.
 *  The direction the play travels INTO the area, in degrees clockwise from true north, from a "from" point to a "to" point, each the area-weighted centroid
 *  (`targetPoint`) of the shapes in force of a group of the hole's areas:
 *  - a green: from the hole's approaches, else its fairways, else its tees, to the green (the approach and the fairway come before the tee for the doglegs);
 *  - a tee: from the tee to the hole's fairways, else to its green;
 *  - a fairway or a rough: from the hole's tees to its green; an approach or a surround: from its fairways, else its tees, to its green;
 *  - UNKNOWN (never guessed, never forced): another type (a bunker…); an area with no shape; an area under no hole (its parent is not a hole: the
 *    practice — a practice relates nothing to anything —, the site root, a loop, the compact course's own level); no green; no "from" area; the two points
 *    under MIN_SPAN_M apart.
 *  "Under a hole" is the area's PARENT being of type `hole` (the nesting rules put the surfaces directly under a hole). Archived areas and areas with no
 *  shape in force take no part. The club corrects it in `geo.area_play_bearing` (area.set_play_bearing@1): the newest row by its moment, then by sync_seq,
 *  is in force; a null bearing goes back to the computed line. The line of play TURNS A DRAWING and nothing else: no proposal, warning or check reads it
 *  (rule 5). The git-ignored survey line is read by nothing; no position of anybody is read (a centroid is a shape's). */

/** The area types the rule above gives a line to (the panel is offered on them alone). */
export const PLAY_LINE_TYPES = ['green', 'tee', 'fairway', 'approach', 'rough', 'surround'] as const
/** Closer than this, two points give no direction worth drawing. */
export const MIN_SPAN_M = 5

export type UnknownReason = 'no_rule' | 'no_shape' | 'no_hole' | 'no_green' | 'no_from' | 'too_close'
export type ComputedLine = { known: true; bearing: number; from: string[]; to: string[] } | { known: false; reason: UnknownReason }
export interface PlayAreaRow { id: string; parent_id: string | null; area_type_code: string; code: string | null; archived_at: string | null }
export interface PlayBearingRow { id: string; area_id: string; bearing_deg: number | null; note: string | null; set_at: string; sync_seq?: number; actor_person_id?: string | null }
export interface PlayLine {
  areaId: string
  /** the bearing in force: the club's when it set one, else the computed one, else null */
  bearing: number | null
  source: 'manual' | 'computed' | 'unknown'
  /** always given, so that a page shows the computed line beside the club's (the doglegs) */
  computed: ComputedLine
  /** the club's row in force (a null bearing included: "back to the computation"), or null when it never corrected this area */
  inForce: PlayBearingRow | null
}

const live = (a: PlayAreaRow) => !a.archived_at

/** The one point of a group of shapes: the area-weighted centroid of their polygons together, else the mean of their own points. */
function groupPoint(geoms: readonly GeoJsonGeometry[]): Pos | null {
  if (!geoms.length) return null
  if (geoms.every((g) => g.type === 'Polygon' || g.type === 'MultiPolygon')) {
    const coordinates = geoms.flatMap((g) => (g.type === 'Polygon' ? [g.coordinates] : g.coordinates)) as unknown[]
    return targetPoint({ type: 'MultiPolygon', coordinates })
  }
  const pts = geoms.map(targetPoint).filter((p): p is Pos => !!p)
  return pts.length ? [pts.reduce((s, p) => s + p[0], 0) / pts.length, pts.reduce((s, p) => s + p[1], 0) / pts.length] : null
}

/** The shape in force of every area (the open version). */
export function shapesInForce(versions: ReadonlyArray<Pick<AreaVersionRow, 'area_id' | 'geom' | 'valid_during'>>): Map<string, GeoJsonGeometry> {
  const out = new Map<string, GeoJsonGeometry>()
  for (const v of versions) if (isOpen(v)) out.set(v.area_id, v.geom)
  return out
}

/** The line of play the course tree gives an area (rules in the header). */
export function computedLine(areaId: string, areas: readonly PlayAreaRow[], shapes: ReadonlyMap<string, GeoJsonGeometry>): ComputedLine {
  const self = areas.find((a) => a.id === areaId)
  const type = self?.area_type_code
  if (!self || !shapes.has(areaId)) return { known: false, reason: 'no_shape' }
  if (!(PLAY_LINE_TYPES as readonly string[]).includes(type!)) return { known: false, reason: 'no_rule' }
  const parent = self.parent_id ? areas.find((a) => a.id === self.parent_id) : undefined
  if (!parent || parent.area_type_code !== 'hole') return { known: false, reason: 'no_hole' }
  const ofType = (t: string) => areas.filter((a) => a.parent_id === parent.id && a.area_type_code === t && live(a) && shapes.has(a.id)).map((a) => a.id).sort()
  const first = (...types: string[]) => { for (const t of types) { const ids = ofType(t); if (ids.length) return ids } return [] as string[] }
  let from: string[], to: string[]
  if (type === 'tee') {
    from = [areaId]; to = first('fairway', 'green')
    if (!to.length) return { known: false, reason: 'no_green' }
  } else {
    to = type === 'green' ? [areaId] : ofType('green')
    if (!to.length) return { known: false, reason: 'no_green' }
    from = type === 'green' ? first('approach', 'fairway', 'tee') : type === 'fairway' || type === 'rough' ? first('tee') : first('fairway', 'tee')
    if (!from.length) return { known: false, reason: 'no_from' }
  }
  const a = groupPoint(from.map((id) => shapes.get(id)!)), b = groupPoint(to.map((id) => shapes.get(id)!))
  if (!a || !b) return { known: false, reason: 'no_shape' }
  if (distanceM(a, b) < MIN_SPAN_M) return { known: false, reason: 'too_close' }
  return { known: true, bearing: bearingDeg(a, b), from, to }
}

/** The club's row in force for an area: the newest by its moment, then by sync_seq; null when it never corrected it. */
export function manualInForce<R extends PlayBearingRow>(rows: readonly R[], areaId: string): R | null {
  let best: R | null = null
  for (const r of rows) {
    if (r.area_id !== areaId) continue
    if (!best || r.set_at > best.set_at || (r.set_at === best.set_at && (r.sync_seq ?? Number.MAX_SAFE_INTEGER) > (best.sync_seq ?? Number.MAX_SAFE_INTEGER))) best = r
  }
  return best
}

/** An area's line of play: the club's bearing in force, else the computed one, else unknown. */
export function playLine(areaId: string, areas: readonly PlayAreaRow[], shapes: ReadonlyMap<string, GeoJsonGeometry>, rows: readonly PlayBearingRow[]): PlayLine {
  const computed = computedLine(areaId, areas, shapes)
  const inForce = manualInForce(rows, areaId)
  if (inForce && inForce.bearing_deg !== null) return { areaId, bearing: inForce.bearing_deg, source: 'manual', computed, inForce }
  if (computed.known) return { areaId, bearing: computed.bearing, source: 'computed', computed, inForce }
  return { areaId, bearing: null, source: 'unknown', computed, inForce }
}

/** Every live area's line of play at once (the map). */
export function playLines(areas: readonly PlayAreaRow[], shapes: ReadonlyMap<string, GeoJsonGeometry>, rows: readonly PlayBearingRow[]): Map<string, PlayLine> {
  const byArea = new Map<string, PlayBearingRow[]>()
  for (const r of rows) byArea.set(r.area_id, [...(byArea.get(r.area_id) ?? []), r])
  const out = new Map<string, PlayLine>()
  for (const a of areas) if (live(a)) out.set(a.id, playLine(a.id, areas, shapes, byArea.get(a.id) ?? []))
  return out
}

/** The codes of the areas a computed line came from and went to (« calculé : FR1-F1 → FR1-G »); an area with no code is left out. */
export function sourceCodes(c: ComputedLine, areas: readonly PlayAreaRow[]): { from: string[]; to: string[] } | null {
  if (!c.known) return null
  const code = (id: string) => areas.find((a) => a.id === id)?.code ?? null
  return { from: c.from.map(code).filter((x): x is string => !!x), to: c.to.map(code).filter((x): x is string => !!x) }
}

/** A bearing as a whole number of degrees in 0…359 (what the club's correction stores). */
export const wholeDegrees = (b: number): number => ((Math.round(b) % 360) + 360) % 360

export const useAreaPlayBearings = () => useMerged<PlayBearingRow>('area_play_bearing', 'geo.area_play_bearing')

/** Every area's line of play from the device's copy (the course, its shapes in force, the club's corrections); null until the store has answered. */
export function usePlayLines(): { lines: Map<string, PlayLine>; areas: readonly PlayAreaRow[]; shapes: Map<string, GeoJsonGeometry>; rows: readonly PlayBearingRow[] } | null {
  const areas = useAreas(); const versions = useAreaVersions(); const rows = useAreaPlayBearings()
  return useMemo(() => {
    if (!areas || !versions || !rows) return null
    const shapes = shapesInForce(versions)
    return { lines: playLines(areas, shapes, rows), areas, shapes, rows }
  }, [areas, versions, rows])
}
